import {
    createContext,
    useContext,
    useLayoutEffect,
    useSyncExternalStore,
} from 'react';

export type SelectedMap = { [key: string]: boolean };

export type SelectionStore = {
    subscribe: (listener: () => void) => () => void;
    getSnapshot: () => SelectedMap;
    setSelected: (next: SelectedMap) => void;
};

const EMPTY_SELECTED: SelectedMap = {};
const NO_SELECTION_IDS: Array<string | number> = [];

export const SelectionStoreContext = createContext<SelectionStore | null>(null);

export const createSelectionStore = (
    initial: SelectedMap = EMPTY_SELECTED,
): SelectionStore => {
    let selected = initial;
    const listeners = new Set<() => void>();

    return {
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        getSnapshot() {
            return selected;
        },
        setSelected(next) {
            if (next === selected) return;
            selected = next;
            listeners.forEach(listener => listener());
        },
    };
};

export const isSelectionActive = (
    selected: SelectedMap,
    ids: Array<string | number>,
) => ids.some(id => !!selected[id]);

const subscribeNoop = () => () => {};

export const useGridPostSelected = (
    selectionIds: Array<string | number> | undefined,
    fallback: boolean,
) => {
    const store = useContext(SelectionStoreContext);
    const ids = selectionIds ?? NO_SELECTION_IDS;
    const getSelection = () =>
        store ? isSelectionActive(store.getSnapshot(), ids) : fallback;

    const selected = useSyncExternalStore(
        store ? store.subscribe : subscribeNoop,
        getSelection,
        getSelection,
    );

    if (!store || !selectionIds) {
        return fallback;
    }

    return selected;
};

export const SelectionSync = ({
    store,
    selected,
}: {
    store: SelectionStore;
    selected: SelectedMap;
}) => {
    useLayoutEffect(() => {
        store.setSelected(selected);
    }, [store, selected]);

    return null;
};
